import { RenderFunction } from "vue"
import { RouteRecord, RouteRecordRaw } from "vue-router"
export type toDataURLImgType = "image/jpeg" | "image/webp" | "image/png"
import { LargeDeviation, SmallDeviation } from "@/data/common"
import { dataType } from "@/data/common"
import { i18n } from "./i18n"
import { useUserInfo } from "@/store"
import { useDeviceStore } from "@/store/device"
import { getOpList } from "@/data/permission"
import { getCurrentRegion } from "./storage"
const t = i18n.global.t
function imgOnload(img: HTMLImageElement, imgType: toDataURLImgType = "image/jpeg"): Promise<string> {
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvasIns = document.createElement("canvas")
      const ctx = canvasIns.getContext("2d")
      canvasIns.height = img.height
      canvasIns.width = img.width
      ctx?.drawImage(img, 0, 0)
      const dataUrl = canvasIns.toDataURL(imgType)
      resolve(dataUrl)
    }
  })
}

/**
 * Get the image and convert it to base 64 format (promisify)
 * @param {string} img url
 * @param {string} imgType
 * @returns {string}
 *
 */
export async function getImg2Base64(url: string, imgType: toDataURLImgType = "image/jpeg"): Promise<string | null> {
  try {
    const img: HTMLImageElement = new Image()
    img.src = url
    img.crossOrigin = "anonymous"
    const dataUrl = await imgOnload(img, imgType)
    return dataUrl
  } catch (err) {
    return null
  }
}

/**
 *
 * @param {String} src
 * @param {Boolean} crossOrigin
 * @returns promise.then
 */
export function createImg(src: string, crossOrigin: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = src
    if (crossOrigin) img.crossOrigin = "*"
    img.onload = () => {
      resolve(img)
    }
    img.onerror = () => {
      reject(false)
    }
  })
}

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string | null}
 */
export function parseTime(time: any, cFormat?: any) {
  if (arguments.length === 0 || !time) {
    return null
  }
  const format = cFormat || "{y}-{m}-{d} {h}:{i}:{s}"
  let date
  if (typeof time === "object") {
    date = time
  } else {
    if (typeof time === "string") {
      if (/^[0-9]+$/.test(time)) {
        // support "1548221490638"
        time = parseInt(time)
      } else {
        // support safari
        // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        time = time.replace(new RegExp(/-/gm), "/")
      }
    }

    if (typeof time === "number" && time.toString().length === 10) {
      time = time * 1000
    }
    date = new Date(time)
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  }
  const time_str = format.replace(/{([ymdhisa])+}/g, (result: any, key: keyof typeof formatObj) => {
    const value = formatObj[key]
    // Note: getDay() returns 0 on Sunday
    if (key === "a") {
      return ["日", "一", "二", "三", "四", "五", "六"][value]
    }
    return value.toString().padStart(2, "0")
  })
  return time_str
}
export const formatIsoTime = (str: string) => {
  const date = new Date(str)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  const hour = String(date.getHours()).padStart(2, "0")
  const minute = String(date.getMinutes()).padStart(2, "0")
  const second = String(date.getSeconds()).padStart(2, "0")
  const formattedDate = `${year}-${month}-${day} ${hour}:${minute}:${second}`
  return formattedDate
}

// export const renderIcon = (icon: Component): RenderFunction => {
//   return () => {
//     return h(NIcon, null, {
//       default: () => h(icon)
//     })
//   }
// }
export const renderIconfont = (iconClass: string): RenderFunction => {
  return () => {
    return h("i", { class: "iconfont " + iconClass })
  }
}

export const cyclicXorByKey = (buffer: string, key: string) => {
  let index = 0
  let len = 0
  let encryption = ""
  let charCode = 0

  while (len < buffer.length) {
    const charBuffer = buffer.charCodeAt(len)
    const charSanthings = key.charCodeAt(index)
    charCode = charBuffer ^ charSanthings
    encryption += String.fromCharCode(charCode)

    len++
    index++
    if (index == key.length) {
      index = 0
    }
  }
  return encryption
}

export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split("; ")
  console.log(cookies)
  for (let i = 0; i < cookies.length; i++) {
    let parts = cookies[i].split("=")
    if (parts[0] === name) {
      return decodeURIComponent(parts[1])
    }
  }
  return null
}

export const clearAllCookie = () => {
  var keys = document.cookie.match(/[^ =;]+(?=\=)/g)
  if (keys) {
    for (var i = keys.length; i--; ) document.cookie = keys[i] + "=0;expires=" + new Date(0).toUTCString()
  }
}

// obj to url
export const parseQueryStr = (obj: any) => {
  let str = ""
  for (let key in obj) {
    if (obj[key] !== undefined && obj[key] !== null) {
      const encodedName = encodeURIComponent(obj[key])
      str += `${key}=${encodedName}&`
    }
  }
  return str.slice(0, -1)
}

export const calculateInnerBoxSize = (outerWidth: number, outerHeight: number, rate: number) => {
  const outerRatio = outerWidth / outerHeight
  let innerWidth, innerHeight

  if (outerRatio > rate) {
    innerWidth = outerWidth
    innerHeight = innerWidth / rate

    if (innerHeight > outerHeight) {
      innerHeight = outerHeight
      innerWidth = innerHeight * rate
    }
  } else {
    innerHeight = outerHeight
    innerWidth = innerHeight * rate

    if (innerWidth > outerWidth) {
      innerWidth = outerWidth
      innerHeight = innerWidth / rate
    }
  }

  return {
    width: innerWidth,
    height: innerHeight
  }
}

export const getToken = () => {
  return localStorage.getItem("TOKEN")
}
export const getHost = () => {
  return localStorage.getItem("HOST")
}

export const getWssHost = (type: "template" | "project" = "template") => {
  const url = getHost() || ""
  const host = new URL(url).host
  const wssUrl = type == "template" ? `wss://${host}/wslink1/` : `wss://${host}/wslink2/`
  console.log(wssUrl)
  return wssUrl
}

export const getUserInfo = () => {
  const str = localStorage.getItem("USER_INFO")
  if (str && str !== "undefined") {
    return JSON.parse(str)
  } else {
    return undefined
  }
}

export const setUserInfo = (obj: any) => {
  localStorage.setItem("USER_INFO", JSON.stringify(obj))
}
export const removeUserInfo = () => {
  localStorage.removeItem("USER_INFO")
}

export const setToken = (token: string, refreshToken: string) => {
  localStorage.setItem("TOKEN", token)
  localStorage.setItem("REFRESHTOKEN", refreshToken)
  return getToken()
}
export const getAllToken = () => {
  return {
    token: localStorage.getItem("TOKEN"),
    rToken: localStorage.getItem("REFRESHTOKEN")
  }
}
export const removeToken = () => {
  localStorage.removeItem("TOKEN")
}
export const getRouteNameItem = (targetName: string, routeArray: Array<RouteRecordRaw>) => {
  for (const route of routeArray) {
    if (route.name === targetName) {
      return route
    }
    if (route.children && route.children.length > 0) {
      const childResult: any = getRouteNameItem(targetName, route.children)
      if (childResult !== null) {
        return childResult
      }
    }
  }
  return null
}

export const convertCamelToKebab = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
}
export const trafficConvert = (value: number) => {
  const units = ["B", "KB", "MB", "GB", "TB"]
  let i = 0
  while (value >= 1024) {
    if (i === 4) break
    value /= 1024
    i++
  }
  //最小MB
  if (i < 2) {
    value /= Math.pow(1024, 2 - i)
    i = 2
  }
  return {
    value: value.toFixed(2),
    unit: units[i]
  }
}

export const sumArray = (numbers: number[]): number => {
  return numbers.reduce((acc, curr) => acc + curr, 0)
}

export const arraysEqual = (arr1: any[], arr2: any[]) => {
  return arr1.length === arr2.length && arr1.every((val: any, idx: number) => val === arr2[idx])
}

export const isKong = (data: any) => data === null || data === undefined || data === ""
export const parseString = (string: string, type: number) => {
  //提取>,<,== 后的数字
  const regexValue = /(?:==|>|<)\s*\(?\s*([+-]?\d+(?:\.\d+)?)\s*\)?/g
  //提取abs()中的内容
  const regexAbs = /abs\(\s*([^)]*?)\s*\)/
  //提取-后的数字
  const regexRule = /-\s*\(?\s*([+-]?\d+(?:\.\d+)?)\s*\)?/g
  //提取${}中的内容
  const regexTagId = /\$\{([^}]*)\}/g
  const data = {
    tagId: 0,
    alarmValue: "", //告警值
    alarmRule: "" //偏差值
  }
  //大小偏差
  if ([LargeDeviation, SmallDeviation].includes(type)) {
    const absContent = regexAbs.exec(string)?.[1] || ""
    data.alarmValue = regexRule.exec(absContent)?.[1] || ""

    data.alarmRule = regexValue.exec(string)?.[1] || ""
    data.tagId = Number(regexTagId.exec(string)?.[1]) || 0
  } else {
    data.alarmValue = regexValue.exec(string)?.[1] || ""
    data.tagId = Number(regexTagId.exec(string)?.[1]) || 0
  }
  return data
}

/**
 * 校验文本是否符合指定数据类型的格式
 * @param text 要校验的文本
 * @param type 数据类型 (0-9，对应 dataType 的键)
 * @returns 校验结果: { success: boolean; message?: string }
 */
export const validateTextByTypeOld = (
  text: string,
  type: number,
  label: "alarmValue" | "alarmRule" | "alarmTarget"
): { success: boolean; message?: string } => {
  const typeName = dataType[type]
  console.log("值", text, type, typeName)
  const map = {
    alarmValue: t("alarm.notMatch1"),
    alarmRule: t("alarm.notMatch2"),
    alarmTarget: t("alarm.notMatch3")
  }
  // 去除首尾空格
  const trimmedText = text.toString().trim()

  if (label == "alarmValue") {
    if (!typeName) {
      return { success: false, message: map[label] }
    }

    // 根据类型进行校验
    switch (typeName) {
      // 布尔型 (0/1)
      case "REG_TYPE_BOOL":
        if (/^(0|1)$/i.test(trimmedText)) {
          return { success: true }
        }
        return { success: false, message: map[label] }

      // 无符号整数 (U16/U32/U64)
      case "REG_TYPE_U16":
      case "REG_TYPE_U32":
      case "REG_TYPE_U64":
        if (/^\d+$/.test(trimmedText)) {
          const num = BigInt(trimmedText)
          // 检查范围 (U16: 0-65535, U32: 0-4294967295, U64: 0-18446744073709551615)
          const maxMap = {
            REG_TYPE_U16: BigInt(65535),
            REG_TYPE_U32: BigInt(4294967295),
            REG_TYPE_U64: BigInt("18446744073709551615")
          }
          if (num >= 0 && num <= maxMap[typeName]) {
            return { success: true }
          }
          return { success: false, message: map[label] }
        }
        return { success: false, message: map[label] }

      // 有符号整数 (S16/S32/S64)
      case "REG_TYPE_S16":
      case "REG_TYPE_S32":
      case "REG_TYPE_S64":
        if (/^-?\d+$/.test(trimmedText)) {
          const num = BigInt(trimmedText)
          // 检查范围 (S16: -32768~32767, S32: -2147483648~2147483647, S64: -9223372036854775808~9223372036854775807)
          const rangeMap = {
            REG_TYPE_S16: { min: BigInt(-32768), max: BigInt(32767) },
            REG_TYPE_S32: { min: BigInt(-2147483648), max: BigInt(2147483647) },
            REG_TYPE_S64: { min: BigInt("-9223372036854775808"), max: BigInt("9223372036854775807") }
          }
          const { min, max } = rangeMap[typeName]
          if (num >= min && num <= max) {
            return { success: true }
          }
          return { success: false, message: map[label] }
        }
        return { success: false, message: map[label] }

      // 浮点数 (FLOAT/DOUBLE)
      case "REG_TYPE_FLOAT":
      case "REG_TYPE_DOUBLE":
        if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmedText)) {
          return { success: true }
        }
        return { success: false, message: map[label] }

      // 字符串 (直接通过)
      case "REG_TYPE_STR":
        return { success: true }

      default:
        return { success: false, message: map[label] }
    }
  } else {
    if (/^-?\d+$/.test(trimmedText)) {
      return { success: true }
    } else {
      return { success: false, message: map[label] }
    }
  }
}

export const validateTextByType = (
  text: string,
  type: number,
  label: "alarmValue" | "alarmRule" | "alarmTarget"
): { success: boolean; message?: string } => {
  const typeName = dataType[type]
  console.log("值", text, type, typeName)
  const map = {
    alarmValue: t("alarm.notMatch1"),
    alarmRule: t("alarm.notMatch2"),
    alarmTarget: t("alarm.notMatch3")
  }
  // 去除首尾空格
  const trimmedText = text.toString().trim()

  if (label == "alarmValue") {
    if (!typeName) {
      return { success: false, message: map[label] }
    }

    // 根据类型进行校验
    switch (typeName) {
      // 布尔型 (0/1)
      case "BOOL":
        if (/^(0|1)$/i.test(trimmedText)) {
          return { success: true }
        }
        return { success: false, message: map[label] }

      // 无符号整数 (U16/U32/U64)
      case "UINT16":
      case "UINT32":
      case "UINT64":
        if (/^\d+$/.test(trimmedText)) {
          const num = BigInt(trimmedText)
          // 检查范围 (U16: 0-65535, U32: 0-4294967295, U64: 0-18446744073709551615)
          const maxMap = {
            UINT16: BigInt(65535),
            UINT32: BigInt(4294967295),
            UINT64: BigInt("18446744073709551615")
          }
          if (num >= 0 && num <= maxMap[typeName]) {
            return { success: true }
          }
          return { success: false, message: map[label] }
        }
        return { success: false, message: map[label] }

      // 有符号整数 (S16/S32/S64)
      case "INT16":
      case "INT32":
      case "INT64":
        if (/^-?\d+$/.test(trimmedText)) {
          const num = BigInt(trimmedText)
          // 检查范围 (S16: -32768~32767, S32: -2147483648~2147483647, S64: -9223372036854775808~9223372036854775807)
          const rangeMap = {
            INT16: { min: BigInt(-32768), max: BigInt(32767) },
            INT32: { min: BigInt(-2147483648), max: BigInt(2147483647) },
            INT64: { min: BigInt("-9223372036854775808"), max: BigInt("9223372036854775807") }
          }
          const { min, max } = rangeMap[typeName]
          if (num >= min && num <= max) {
            return { success: true }
          }
          return { success: false, message: map[label] }
        }
        return { success: false, message: map[label] }

      // 浮点数 (FLOAT/DOUBLE)
      case "REAL":
      case "LREAL":
        if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmedText)) {
          return { success: true }
        }
        return { success: false, message: map[label] }

      // 字符串 (直接通过)
      case "STRING":
        return { success: true }

      default:
        return { success: false, message: map[label] }
    }
  } else {
    if (/^-?\d+$/.test(trimmedText)) {
      return { success: true }
    } else {
      return { success: false, message: map[label] }
    }
  }
}

export async function exportExcel(api: any, params: any, fileName: string) {
  try {
    const tz = 0 - new Date().getTimezoneOffset() / 60
    params.tz = tz
    let res = await api(params)
    if (res) {
      console.log(res)
      const _fileName = `${fileName}.xlsx`
      const elink = document.createElement("a")
      elink.download = _fileName
      elink.style.display = "none"
      elink.href = URL.createObjectURL(res)
      document.body.appendChild(elink)
      elink.click()
      URL.revokeObjectURL(elink.href)
      document.body.removeChild(elink)
    }
  } catch (error) {
    console.log(error)
  }
}

const keyWords = [
  "var",
  "alarm",
  "io",
  "sys",
  "user",
  "varrecord",
  "operation",
  "dbaccess",
  "script",
  "histrecord",
  "and",
  "as",
  "boolean",
  "byref",
  "byte",
  "byval",
  "call",
  "case",
  "class",
  "const",
  "currency",
  "debug",
  "dim",
  "do",
  "double",
  "each",
  "else",
  "elseif",
  "empty",
  "end",
  "endif",
  "enum",
  "eqv",
  "event",
  "exit",
  "false",
  "for",
  "function",
  "get",
  "goto",
  "if",
  "imp",
  "implements",
  "in",
  "integer",
  "is",
  "let",
  "like",
  "long",
  "loop",
  "lset",
  "me",
  "mod",
  "new",
  "next",
  "not",
  "nothing",
  "null",
  "on",
  "option",
  "optional",
  "or",
  "paramarray",
  "preserve",
  "private",
  "public",
  "raiseevent",
  "redim",
  "rem",
  "resume",
  "rset",
  "select",
  "set",
  "shared",
  "single",
  "static",
  "stop",
  "sub",
  "then",
  "to",
  "true",
  "type",
  "typeof",
  "until",
  "variant",
  "wend",
  "while",
  "with",
  "xor",
  "this"
]
export const isValidDevName = (
  name: string
): {
  state: boolean
  errMsg: string | null
} => {
  if (!name) {
    return {
      state: false,
      errMsg: t("common.pi", [t("device.deviceName")])
    }
  }

  // 基础正则校验（禁止数字和下划线开头）
  const pattern1 = /^(?![_0-9]).+$/u
  if (!pattern1.test(name)) {
    return {
      state: false,
      errMsg: t("device.devNameTip")
    }
  }

  // 基础正则校验（只能包含汉字、英文字母、数字、下划线）
  const pattern2 = /^[\p{Script=Han}a-zA-Z0-9_]+$/u
  if (!pattern2.test(name)) {
    return {
      state: false,
      errMsg: t("device.devNameTip2")
    }
  }

  // 关键字校验
  const lowerName = name.toLowerCase()
  const keyWord = keyWords.find((item) => item == lowerName)
  if (keyWord) {
    return {
      state: false,
      errMsg: t("device.noKeyword") + ": " + name
    }
  }

  return {
    state: true,
    errMsg: null
  }
}

// 获取百分数
export function getPercent(numerator: any, denominator: any, num = 1, onlyInteger = false): string {
  // 检查分母是否为 0、NaN、null、undefined 或非数字
  if (!denominator || isNaN(denominator) || !isFinite(denominator)) {
    return "0"
  }

  // 检查分子是否为 NaN 或非数字
  if (isNaN(numerator) || !isFinite(numerator)) {
    return "0"
  }

  const percentage = (numerator / denominator) * 100

  if (onlyInteger) {
    return Math.round(percentage).toString()
  }

  if (Number.isInteger(percentage)) {
    return percentage.toString()
  }

  return percentage.toFixed(num)
}

// 增长率
export function calculateMonthOverMonthGrowth(currentValue: any, previousMonthValue: any, num = 1, withSign = true) {
  // 处理非数字输入
  if (typeof currentValue !== "number" || typeof previousMonthValue !== "number") {
    return 0 // 或者可以抛出错误
  }

  // 如果上月为0，当前也为0，则增长为0%
  if (previousMonthValue === 0 && currentValue === 0) {
    return 0
  }

  // 如果上月为0，当前不为0，则视为无限增长（通常表示为特定值或特殊处理）
  if (previousMonthValue === 0 && currentValue !== 0) {
    return 100
  }

  let growthRate = ((currentValue - previousMonthValue) / previousMonthValue) * 100

  if (!withSign) {
    growthRate = Math.abs(growthRate) // 去掉负号
  }

  // if (Number.isInteger(growthRate)) {
  //   return growthRate
  // }
  return parseFloat(growthRate.toFixed(num))
}

//判断是否有计费通知查看权限
export function getHasBillingPm() {
  const userStore = useUserInfo()
  // await userStore.getUserInfo()
  const hasOpList = userStore.userInfo?.permissions
  // console.log("hasOpList", hasOpList)
  const AllOpObj = getOpList()
  // console.log("AllOpObj", AllOpObj)
  const region = getCurrentRegion()
  // const hasBillingPm = userStore.userInfo?.is_main || hasOpList?.find((op) => AllOpObj[op] == "msgBilling-view")
  const hasBillingPm =
    (userStore.userInfo?.is_main || hasOpList?.find((op) => AllOpObj[op] === "msgBilling-view")) && isShowFlowTip()
  return hasBillingPm
}

//判断是否需要弹流量提示
export function isShowFlowTip() {
  const deviceStore = useDeviceStore()
  const region = getCurrentRegion()
  if (region !== "cn") {
    return true
  } else if (deviceStore.deviceDetail.bindType === 2) {
    return true
  } else {
    return false
  }
}

//获取当前数据中心对应的标签键值
export const getLabelKeyByRegion = (): string => {
  const region = getCurrentRegion()
  switch (region) {
    case "cn":
      return "china"
    case "in":
      return "india"
    case "eu":
      return "europe"
    default:
      return "china"
  }
}

// 判断有效数字
export function isValidNumber(value: any): boolean {
  if (typeof value === 'number') {
    return !isNaN(value)
  }
  if (typeof value === 'string') {
    return value.trim() !== '' && !isNaN(Number(value))
  }
  return false
}

/**
 * 不超过指定位数时不补零；超过时按 toFixed 处理
 * @param value  原始值
 * @param digits 要保留的小数位数（默认保留 0 位）
 * @returns 字符串
 */
export function toFixedSmart(value: number | string, digits: number = 0): string {
  const str = String(value)
  const dot = str.indexOf('.')
  // 没有小数点，或小数位 <= digits，直接返回
  if (dot === -1 || str.length - dot - 1 <= digits) return str

  // 超过 digits 位，走标准四舍五入 + 补零
  return Number(value).toFixed(digits)
}